import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Play, Pause, X, Loader2, UploadCloud, CheckCircle2, Volume2, VolumeX } from "lucide-react";
import { fetchReels, reelUploadStart, reelUploadChunk, reelUploadFinish, formatApiError } from "@/lib/api";

const CHUNK = 8 * 1024 * 1024;

function ReelCard({ reel, soundId, onSound }) {
    const vidRef = useRef(null);
    const cardRef = useRef(null);
    const visibleRef = useRef(false);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(true);
    const isEmbed = reel.provider === "youtube" || reel.provider === "instagram";

    // muted autoplay while the card is in the viewport; pause when it leaves / tab hides
    useEffect(() => {
        if (isEmbed) return undefined;
        const v = vidRef.current, el = cardRef.current;
        if (!v || !el) return undefined;
        v.muted = true; // set the property — React's muted attribute alone is unreliable
        const tryPlay = () => v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        const io = new IntersectionObserver((entries) => {
            visibleRef.current = entries[0].isIntersecting;
            if (entries[0].isIntersecting && !document.hidden) tryPlay();
            else { v.pause(); setPlaying(false); }
        }, { threshold: 0.35 });
        io.observe(el);
        const onVis = () => {
            if (document.hidden) { v.pause(); setPlaying(false); }
            else if (visibleRef.current) tryPlay();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); v.pause(); };
    }, [isEmbed]);

    // another card took the sound -> this one goes back to mute
    useEffect(() => {
        if (isEmbed || soundId === reel.id) return;
        const v = vidRef.current;
        if (v) v.muted = true;
        setMuted(true);
    }, [soundId, isEmbed, reel.id]);

    const togglePlay = () => {
        const v = vidRef.current;
        if (!v) return;
        if (v.paused) { v.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
        else { v.pause(); setPlaying(false); }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const v = vidRef.current;
        if (!v) return;
        const unmuting = v.muted; // element is the source of truth, not state
        v.muted = !unmuting;
        setMuted(!unmuting);
        if (unmuting) {
            onSound(reel.id); // claims the sound — siblings get muted
            if (v.paused) { v.play().then(() => setPlaying(true)).catch(() => {}); }
        } else {
            onSound(null);
        }
    };

    return (
        <div ref={cardRef} className="group snap-start shrink-0 w-40 sm:w-48 rounded-2xl overflow-hidden bg-black relative border border-stone-800" data-testid={`reel-card-${reel.id}`}>
            {isEmbed ? (
                <iframe src={reel.url} title={reel.trip || "Trip reel"} data-testid={`reel-embed-${reel.id}`}
                    className="w-full aspect-[9/16]" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen
                    loading="lazy" referrerPolicy="strict-origin-when-cross-origin" />
            ) : (
                <>
                    <video ref={vidRef} src={reel.url} muted autoPlay loop playsInline preload="metadata" onClick={togglePlay}
                        className="w-full aspect-[9/16] object-cover cursor-pointer" data-testid={`reel-video-${reel.id}`} />
                    <button onClick={togglePlay} data-testid={`reel-toggle-${reel.id}`} aria-label={playing ? "Pause reel" : "Play reel"}
                        className={`absolute inset-0 flex items-center justify-center transition-opacity ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
                        <span className="h-12 w-12 rounded-full bg-saffron/90 flex items-center justify-center">
                            {playing ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white ml-0.5" />}
                        </span>
                    </button>
                    <button onClick={toggleMute} data-testid={`reel-mute-${reel.id}`}
                        aria-label={muted ? "Unmute reel" : "Mute reel"}
                        className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-black/55 hover:bg-saffron border border-white/25 flex items-center justify-center transition-colors">
                        {muted ? <VolumeX className="h-4 w-4 text-white" /> : <Volume2 className="h-4 w-4 text-white" />}
                    </button>
                </>
            )}
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3 pointer-events-none">
                <p className="font-display font-bold text-xs text-white leading-snug line-clamp-2">{reel.trip || "Trip Reel"}</p>
                <p className="text-[10px] text-white/70 mt-0.5">by {reel.person_name}</p>
            </div>
        </div>
    );
}

function ReelUploadModal({ onClose }) {
    const [f, setF] = useState({ name: "", trip: "" });
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const fileRef = useRef(null);

    const submit = async () => {
        if (!f.name.trim() || !file) return toast.error("Add your name and pick a video");
        if (file.size > 150 * 1024 * 1024) return toast.error("Video must be under 150MB");
        setBusy(true);
        try {
            const { upload_id, max_chunk } = await reelUploadStart({ name: f.name, trip: f.trip, filename: file.name, size: file.size });
            const step = Math.min(CHUNK, max_chunk);
            let sent = 0;
            for (let off = 0; off < file.size; off += step) {
                await reelUploadChunk(upload_id, file.slice(off, off + step));
                sent = Math.min(file.size, off + step);
                setProgress(Math.round((sent / file.size) * 100));
            }
            const res = await reelUploadFinish(upload_id);
            setDone(res.reference);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed — please try again");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[70] bg-navy-deep/80 flex items-center justify-center p-4" data-testid="reel-upload-modal">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 relative">
                <button onClick={onClose} data-testid="reel-upload-close" className="absolute top-4 right-4 text-stone-400 hover:text-navy"><X className="h-5 w-5" /></button>
                {done ? (
                    <div className="text-center py-6" data-testid="reel-upload-success">
                        <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
                        <p className="mt-3 font-display font-black text-lg text-navy">Reel received!</p>
                        <p className="mt-1 text-sm text-stone-500">Our team reviews every video before it appears on the site. Reference <b>{done}</b>.</p>
                        <button onClick={onClose} className="mt-5 rounded-full bg-navy text-white text-xs font-black uppercase tracking-wider px-6 py-2.5">Done</button>
                    </div>
                ) : (
                    <>
                        <h3 className="font-display font-black text-lg text-navy">Share your trip reel</h3>
                        <p className="text-xs text-stone-500 mt-1 mb-4">MP4 / MOV / WebM, up to 150MB. Vertical videos look best. Reviewed before publishing.</p>
                        <div className="space-y-3">
                            <input className="w-full rounded-lg border border-stone-300 px-3.5 py-2.5 text-sm outline-none focus:border-saffron" placeholder="Your name *" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="reel-name" />
                            <input className="w-full rounded-lg border border-stone-300 px-3.5 py-2.5 text-sm outline-none focus:border-saffron" placeholder="Which trip? (e.g. Manali Snow Special)" value={f.trip} onChange={(e) => setF((s) => ({ ...s, trip: e.target.value }))} data-testid="reel-trip" />
                            <input ref={fileRef} type="file" accept=".mp4,.mov,.webm" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="reel-file-input" />
                            <button type="button" onClick={() => fileRef.current?.click()} data-testid="reel-pick-file"
                                className="w-full rounded-xl border-2 border-dashed border-stone-300 hover:border-saffron py-6 text-sm font-bold text-stone-500 flex flex-col items-center gap-1.5 transition-colors">
                                <UploadCloud className="h-6 w-6 text-saffron" />
                                {file ? `${file.name} (${Math.round(file.size / 1048576)} MB)` : "Tap to pick your video"}
                            </button>
                            {busy && (
                                <div className="h-2 rounded-full bg-stone-100 overflow-hidden" data-testid="reel-upload-progress">
                                    <div className="h-full bg-saffron transition-all" style={{ width: `${progress}%` }} />
                                </div>
                            )}
                            <button onClick={submit} disabled={busy || !file} data-testid="reel-submit"
                                className="w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-3 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading {progress}%</> : "Submit for review"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export function ReelsSection() {
    const [reels, setReels] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [soundId, setSoundId] = useState(null); // which reel currently owns the speakers
    const load = () => fetchReels().then((r) => setReels(r.reels)).catch(() => setReels([]));
    useEffect(() => { load(); }, []);

    return (
        <section className="bg-navy-deep" data-testid="home-reels">
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="flex items-end justify-between gap-3 mb-6">
                    <div>
                        <p className="font-script text-3xl sm:text-4xl text-gold -rotate-1 inline-block leading-none">straight from the road</p>
                        <h2 className="font-display font-black text-2xl sm:text-3xl text-white mt-1">Trip Reels by Real Travellers</h2>
                        <p className="text-sm text-white/60 mt-1">Unfiltered moments from our trips — sent in by the people who lived them.</p>
                    </div>
                    <button onClick={() => setShowUpload(true)} data-testid="reel-share-btn"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-saffron hover:bg-saffron-dark text-white text-[11px] font-display font-black uppercase tracking-wider px-4 py-2.5 transition-colors">
                        <Clapperboard className="h-3.5 w-3.5" /> Share your reel
                    </button>
                </div>
                {reels === null ? (
                    <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
                ) : (
                    <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="reels-row">
                        {reels.map((r) => <ReelCard key={r.id} reel={r} soundId={soundId} onSound={setSoundId} />)}
                        {!reels.length && (
                            <button onClick={() => setShowUpload(true)} data-testid="reel-empty-cta"
                                className="snap-start shrink-0 w-40 sm:w-48 aspect-[9/16] rounded-2xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-2 text-white/60 hover:text-white hover:border-saffron transition-colors">
                                <UploadCloud className="h-8 w-8" />
                                <span className="text-xs font-bold px-4 text-center">Be the first to share your trip reel</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
            {showUpload && <ReelUploadModal onClose={() => setShowUpload(false)} />}
        </section>
    );
}
